import {AfterViewInit, Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, NgForm} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {interval, ReplaySubject} from "rxjs";
import {UserPermissions} from "src/app/shared/interface/user-permissions";
import {CountedAndPaginated} from "src/app/shared/interface/response/counted-and-paginated";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "src/app/shared/services/auth.service";
import {FilterService} from "src/app/shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "src/app/shared/ngrx/reducers";
import {FormHelperService} from "src/app/shared/services/form-helper.service";
import {delay, distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {getCurrentRouteState} from "src/app/shared/ngrx/selector";
import {RouterStateUrl} from "src/app/shared/ngrx/route-serializer";
import {HiperioUserJob} from "../../../shared/interface/hiperio-user-job";
import {HiperioUserJobsService} from "../../../shared/services/hiperio-user-jobs.service";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import { HIPERIO_USER_JOB_STATUS_CREATED} from "../../../shared/services/dict.service";
import {MatSlideToggleChange} from "@angular/material/slide-toggle";


@Component({
  selector: 'app-user-jobs-list',
  templateUrl: './user-jobs-list.component.html',
  styleUrls: ['./user-jobs-list.component.scss']
})
export class UserJobsListComponent implements AfterViewInit, OnInit, OnDestroy  {
@ViewChild('filtersForm') filtersForm: NgForm;
@ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  jobs: HiperioUserJob[] = [];
  displayedColumns: string[] = ['ofwca' , 'createdAt', 'createdBy', 'status', 'createdAccount', 'action'];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  actionForm: FormGroup;
  updateInProgress = false;

  readonly HIPERIO_USER_JOB_STATUS_CREATED = HIPERIO_USER_JOB_STATUS_CREATED;

private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);


  constructor(
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private fb: FormBuilder,
    private formHelper: FormHelperService,
    private hiperioUserJobsService: HiperioUserJobsService,
) {
    this.userPermissions = auth.getUserPermissions();
    this.hiperioUserJobsService.jobListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<HiperioUserJob>) => {
          this.jobs = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      partner: 'string',
      ofwca: 'string',
      status: 'int',
      assetsRegional: 'string',
      accountCreated: 'int'
    }, filters);
  }

  ngOnInit(): void {
    interval(5000)
.pipe(takeUntil(this.destroyed$))
    .subscribe(() => {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      }
    });
}

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          ...this.buildFilters()
        })]);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;

        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    return filters;
  }

  loadListData(): void {
    this.hiperioUserJobsService.loadJobList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  restart(uuid: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz uruchomić tę akcję?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.hiperioUserJobsService.restart(uuid)
          .subscribe(() => {
            this.loadListData();
          }, (error: HttpErrorResponse) => {
            this.snack.open('Blokada uruchomienia akcji', error.status.toString(), {duration: 2000});
            if (error.status === 400) {
              this.snack.open(error.error.error.text, '400', {duration: 4000});
            }
          });
      }
    });
  }

  formChanged(
    $event: MatSlideToggleChange,
    hiperioUserJob: HiperioUserJob,
    formData: { accountCreated: boolean }
  ): void {
    this.updateInProgress = true;

    this.hiperioUserJobsService.updateAccountCreated(hiperioUserJob.uuid, formData)
      .subscribe(() => {
        this.snack.open('Pomyślnie zakutalizowano', '', {duration: 2000});
        hiperioUserJob.accountCreated = formData.accountCreated;
        this.loadListData();
        this.updateInProgress = false;
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy aktualizacji wystąpił błąd', error.status.toString(), {duration: 2000});
        this.updateInProgress = false;
      });
  }

}
